/**
 * Het ruilscherm van het origineel.
 *
 * Eén paneel voor beide vormen van ruil — met de bank en met je tegenstander — want zo
 * deed de cd het ook: de knop `Bank` wisselt alleen de tegenpartij, de rest van het
 * scherm blijft staan. Opgemeten op `catan-ref/shots/mij/ruilen-met-de-bank.png` en
 * `handelscherm-ruilscherm.png`.
 *
 * ```
 *  ┌──────────────────────────────────────────┐
 *  │            [ Bank / Chi ]                │  kopbalk met de zes grondstoffen
 *  │   🐑  🪨  🪵  🧱  🌾  ⛰                    │  die de tegenpartij heeft
 *  ├──────────────────────────────────────────┤
 *  │  ┌────────┐           ┌────────┐         │  wat jij geeft ← → wat je krijgt
 *  │  │ 🪨🪨🪨 │           │   🐑   │         │  ze vullen zichzelf uit de rij eronder
 *  │  └────────┘           └────────┘         │
 *  ├──────────────────────────────────────────┤
 *  │   ▲   ▲   ▲   ▲   ▲   ▲                  │  van je af: die geef je weg
 *  │  🐑1 🪨1 🪵3 🧱0 🌾2 ⛰2                   │  wat je er na de ruil van overhoudt
 *  │   ▼   ▼   ▼   ▼   ▼   ▼                  │  naar je toe: die wil je hebben
 *  │ [Aanbieden][Nieuw][Annuleren][Bank]      │
 *  └──────────────────────────────────────────┘
 * ```
 *
 * De pijl zegt de richting; er valt geen kant meer te kiezen. Dat scheelt een klik die de
 * helft van de tijd de verkeerde was — juist wat je wilt *vragen* heb je zelf niet.
 */
import { LANDSCAPE_RESOURCE, type Resource } from '@catan/contracts';
import { Container, Sprite, Texture } from 'pixi.js';
import { asset, hasAsset, klik } from './assets.js';
import { knop, tekst, SKIN } from './skin.js';

/** Waar alles staat, in het raster van 1024×768. Opgemeten op de schermafdruk. */
const P = {
  // Opgemeten met een coördinatenraster over `ruilen-met-de-bank.png`. De drie secties
  // zijn kant-en-klare platen van 438 breed; ze staan op ware grootte onder elkaar.
  x: 40,
  y: 62,
  breed: 438,
  kop: { hoog: 72, naamY: 8, iconY: 60, iconX: 60, stap: 52, iconMaat: 22 },
  midden: {
    y: 72,
    hoog: 144,
    // De vakken zitten al in de plaat; dit is waar de iconen erin komen. De twee pijlen
    // tussen de vakken zijn decor: kiezen doe je met de pijlen bij de grondstoffen zelf.
    geef: { x: 20, y: 21, breed: 115, hoog: 90 },
    krijg: { x: 288, y: 21, breed: 115, hoog: 90 },
  },
  tellers: { y: 216, hoog: 169, opY: 10, iconY: 42, neerY: 72, x: 60, stap: 52, breed: 19, hoog2: 29 },
  knoppen: { y: 336, plekken: [12, 120, 228, 335] },
} as const;

/**
 * De zes grondstoffen in de volgorde van de statusbalk.
 *
 * Die volgorde moet gelijk zijn aan `BALK_SYMBOLEN` in `hud.ts`, anders staat dezelfde
 * grondstof in het ruilscherm op een andere plek dan in je voorraadbalk. Goud en klei
 * stonden hier omgedraaid, en dan lees je de balk en het scherm naast elkaar en zie je
 * getallen die elkaar lijken tegen te spreken.
 */
export const SOORTEN: readonly Resource[] = ['wol', 'erts', 'hout', 'goud', 'graan', 'klei'];

/** Wat de bank per grondstof vraagt, als je met de bank ruilt. */
export interface Saldogrenzen {
  /** Wat jij van deze grondstof hebt liggen. */
  readonly heb: number;
  /** Wat de tegenpartij ervan heeft. Bij de bank telt dit niet mee. */
  readonly zijHebben: number;
  /** De bankkoers, of niets als je met een speler ruilt. */
  readonly koers?: number;
}

/**
 * Het saldo van één grondstof, na een klik op een pijl.
 *
 * Elke grondstof heeft één getal dat door nul heen loopt: **positief is wat je erbij wilt,
 * negatief is wat je weggeeft.** Zo hoef je niet eerst een vak te kiezen — de pijl die je
 * aanklikt zegt al welke kant het op gaat. De pijl bóven een grondstof wijst van je af en
 * geeft hem dus weg (`richting -1`); de pijl eronder wijst naar je toe en vraagt hem
 * (`richting +1`). Dezelfde pijl de andere kant op haalt het weer weg, dus door nul heen
 * loopt het vanzelf van geven naar vragen.
 *
 * Hiervoor moest je eerst met een pijl in het midden kiezen wélk vak je vulde. Dat was één
 * klik te veel, en de helft van de tijd de verkeerde: de grondstoffen die je wilt vrágen
 * zijn juist de grondstoffen die je niet hebt.
 *
 * Bij de bank gaat het geven met de koers tegelijk — drie erts voor één wol is één klik,
 * niet drie — en krijgen kan er maar één zijn, want dat is wat de bank teruggeeft.
 */
export function nieuwSaldo(saldo: number, richting: 1 | -1, grenzen: Saldogrenzen): number {
  const { heb, zijHebben, koers } = grenzen;
  const uit =
    koers === undefined
      ? Math.max(-heb, Math.min(zijHebben, saldo + richting))
      : // Bij de bank kun je alleen een veelvoud van de koers weggeven, en er komt er één
        // terug.
        richting < 0
        ? saldo > 0
          ? saldo - 1
          : Math.max(-Math.floor(heb / koers) * koers, saldo - koers)
        : saldo < 0
          ? Math.min(0, saldo + koers)
          : Math.min(1, saldo + 1);
  // `Math.max(-0, …)` levert min nul op, en min nul is geen saldo.
  return uit === 0 ? 0 : uit;
}

export interface RuilRegels {
  /** Wat jij van elke grondstof hebt liggen. */
  readonly voorraad: Readonly<Record<Resource, number>>;
  /** Wat de tegenstander heeft (alleen voor de kopbalk in spelersmodus). */
  readonly vanHen: Readonly<Record<Resource, number>>;
  /** De bankkoers per grondstof: hoeveel je er van weg moet geven voor één naar keuze. */
  readonly koers: Readonly<Record<Resource, number>>;
  readonly tegenstander: string;
}

export interface RuilOpties {
  readonly regels: RuilRegels;
  /**
   * Een aanbod dat de tegenstander doet.
   *
   * Het origineel opent hiervoor geen apart schermpje: ditzelfde paneel springt open met
   * de ruil er al in, en jij accepteert, past hem aan als tegenvoorstel, of wijst hem af.
   * `give` is dan wat *hij* geeft en dus wat jij krijgt.
   */
  readonly binnengekomen?: {
    readonly give: Partial<Record<Resource, number>>;
    readonly want: Partial<Record<Resource, number>>;
  };
  /** Alleen bij een binnengekomen aanbod: aannemen of afwijzen. */
  readonly onAntwoord?: (accepteren: boolean) => void;
  /** Ruil met de bank uitvoeren: zoveel van deze soort weg, één van die soort terug. */
  readonly onBank: (geef: Resource, aantal: number, krijg: Resource) => void;
  /** Een aanbod aan de tegenstander doen. */
  readonly onAanbod: (
    geef: Partial<Record<Resource, number>>,
    wil: Partial<Record<Resource, number>>,
  ) => void;
  readonly onSluiten: () => void;
}

/**
 * Bouwt het paneel.
 *
 * Het paneel houdt zijn eigen invoer bij (wat je geeft, wat je vraagt, tegen wie) en
 * hertekent zichzelf; pas als je op `Aanbieden` drukt gaat er een zet naar het spel.
 * Zo blijft de regelkernel buiten het geklik.
 */
export function ruilPaneel(opties: RuilOpties): Container {
  const wortel = new Container();
  wortel.x = P.x;
  wortel.y = P.y;

  // Komt het aanbod van de overkant, dan staat het paneel meteen op de spelersmodus met
  // zijn voorstel erin: wat hij geeft staat bij "krijg", wat hij vraagt bij "geef".
  const binnen = opties.binnengekomen;
  // Het scherm opent op je tegenstander — zo staat het in het handboek ("Met de
  // tegenstander handelen") en zo laat `catan-ref/shots/mij/handelscherm-ruilscherm.png`
  // het zien: zijn naam in de kopbalk, met een knop "Bank" om over te schakelen. Eerst
  // je tegenstander proberen en pas dan de bank is ook de volgorde van de regels.
  let metBank = false;
  /**
   * Waarom er niets gebeurde.
   *
   * Er blijft één klik over die niets kán doen: een grondstof die niemand heeft. Zonder
   * uitleg is dat niet te onderscheiden van een kapot scherm — dat is precies hoe het
   * overkwam. Nu staat er waaróm.
   */
  let melding: string | undefined;
  const geef: Partial<Record<Resource, number>> = { ...(binnen?.want ?? {}) };
  const wil: Partial<Record<Resource, number>> = { ...(binnen?.give ?? {}) };
  /** Zolang je niets verandert, is "Aanbieden" gewoon "Accepteren". */
  let ongewijzigd = binnen !== undefined;

  const laag = new Container();
  wortel.addChild(laag);

  const icoon = (soort: Resource, x: number, y: number, maat = 24): Sprite | undefined => {
    if (!hasAsset('symbolen', soort)) return undefined;
    const s = new Sprite(Texture.from(asset('symbolen', soort).src));
    s.setSize(maat, maat);
    s.x = x;
    s.y = y;
    return s;
  };

  /** Een pijlknop uit de skin, met zijn drie toestanden. */
  const pijl = (
    namen: readonly [string, string, string],
    x: number,
    y: number,
    breed: number,
    hoog: number,
    onClick: () => void,
  ): Container => {
    const doos = new Container();
    const beeld = new Sprite(Texture.from(asset('skin', namen[0]).src));
    beeld.setSize(breed, hoog);
    doos.addChild(beeld);
    doos.x = x;
    doos.y = y;
    doos.eventMode = 'static';
    doos.cursor = 'pointer';
    const zet = (i: number): void => {
      beeld.texture = Texture.from(asset('skin', namen[Math.min(i, 2)] ?? namen[0]).src);
    };
    doos.on('pointerover', () => zet(1));
    doos.on('pointerout', () => zet(0));
    doos.on('pointerdown', () => zet(2));
    doos.on('pointertap', () => {
      klik();
      onClick();
      teken();
    });
    return doos;
  };

  /** Wat er met deze grondstof gebeurt: positief is erbij, negatief is weg. */
  const saldoVan = (soort: Resource): number => (wil[soort] ?? 0) - (geef[soort] ?? 0);

  const zetSaldo = (soort: Resource, saldo: number): void => {
    delete geef[soort];
    delete wil[soort];
    if (saldo > 0) wil[soort] = saldo;
    else if (saldo < 0) geef[soort] = -saldo;
  };

  /**
   * Een klik op een pijl: `1` is de pijl eronder (naar je toe, dus vragen), `-1` de pijl
   * erboven (van je af, dus geven).
   */
  const stap = (soort: Resource, richting: 1 | -1): void => {
    ongewijzigd = false;
    const heb = opties.regels.voorraad[soort] ?? 0;
    const zijHebben = opties.regels.vanHen[soort] ?? 0;
    const was = saldoVan(soort);
    const saldo = nieuwSaldo(was, richting, {
      heb,
      zijHebben,
      ...(metBank ? { koers: opties.regels.koers[soort] } : {}),
    });

    // Waarom er niets gebeurde. Bij de bank kan dat ook zijn dat je er te weinig hebt voor
    // één koers; dan blijft de teller staan en is er zonder uitleg niets te zien.
    melding = undefined;
    if (saldo === was) {
      if (richting > 0) {
        melding = metBank
          ? `de bank geeft er maar één terug`
          : zijHebben === 0
            ? `${opties.regels.tegenstander} heeft geen ${soort}`
            : `meer ${soort} dan ${zijHebben} is er niet`;
      } else {
        melding = metBank
          ? `voor een ruil heb je ${opties.regels.koers[soort]} ${soort} nodig`
          : heb === 0
            ? `je hebt zelf geen ${soort}`
            : `meer ${soort} dan ${heb} heb je niet`;
      }
    }

    // Bij de bank ruil je één soort tegen één soort; een tweede keuze vervangt de eerste.
    if (metBank) {
      for (const s of SOORTEN) {
        if (s === soort) continue;
        if (saldo < 0) delete geef[s];
        if (saldo > 0) delete wil[s];
      }
    }
    zetSaldo(soort, saldo);
  };

  /** De iconen die in een van de twee vakken terecht zijn gekomen. */
  const vulVak = (
    bundel: Partial<Record<Resource, number>>,
    vak: { x: number; y: number; breed: number; hoog: number },
  ): void => {
    let n = 0;
    for (const soort of SOORTEN) {
      for (let i = 0; i < (bundel[soort] ?? 0); i++) {
        const s = icoon(
          soort,
          vak.x + 6 + (n % 4) * 26,
          P.midden.y + vak.y + 6 + Math.floor(n / 4) * 26,
          24,
        );
        if (s) laag.addChild(s);
        n += 1;
      }
    }
  };

  function teken(): void {
    laag.removeChildren();

    const plaat = (naam: string, y: number): void => {
      const bron = asset('skin', naam);
      const s = new Sprite(Texture.from(bron.src));
      s.y = y;
      laag.addChild(s);
    };

    // --- de drie secties, op ware grootte onder elkaar
    plaat(metBank ? SKIN.ruilKopBank : SKIN.ruilKopSpeler, 0);
    plaat(SKIN.ruilMidden, P.midden.y);
    plaat(SKIN.ruilTellers, P.tellers.y);

    // --- kopbalk: met wie ruil je?
    const kopLabel = tekst(metBank ? 'Bank' : opties.regels.tegenstander, 16);
    kopLabel.anchor.set(0.5, 0);
    kopLabel.x = P.breed / 2;
    kopLabel.y = P.kop.naamY;
    laag.addChild(kopLabel);

    // De bankbalk is leeg en krijgt de iconen erbij; de spelersbalk heeft ze al staan en
    // krijgt alleen de aantallen.
    for (const [i, soort] of SOORTEN.entries()) {
      const x = P.kop.iconX + i * P.kop.stap;
      if (metBank) {
        const s = icoon(soort, x, P.kop.iconY - 22, P.kop.iconMaat);
        if (s) laag.addChild(s);
      } else {
        const label = tekst(String(opties.regels.vanHen[soort]), 13);
        label.x = x + 24;
        label.y = P.kop.iconY - 20;
        laag.addChild(label);
      }
    }

    // --- middenvak: wat gaat er heen en weer? Het vult zichzelf uit de pijlen onderaan;
    // hier valt niets meer te kiezen.
    vulVak(geef, P.midden.geef);
    vulVak(wil, P.midden.krijg);

    for (const [vak, opschrift] of [
      [P.midden.geef, 'jij geeft'],
      [P.midden.krijg, metBank ? 'je krijgt' : `${opties.regels.tegenstander} geeft`],
    ] as const) {
      const label = tekst(opschrift, 11, 0xffd257);
      label.anchor.set(0.5, 1);
      label.x = vak.x + vak.breed / 2;
      label.y = P.midden.y + vak.y - 3;
      laag.addChild(label);
    }

    if (melding !== undefined) {
      const uitleg = tekst(melding, 11, 0xe8a15a);
      uitleg.anchor.set(0.5, 0);
      uitleg.x = P.breed / 2;
      uitleg.y = P.midden.y + P.midden.hoog - 22;
      laag.addChild(uitleg);
    }

    // --- tellerrij: de iconen staan al op de plaat, wij zetten de pijlen en getallen
    //
    // De pijl bóven een grondstof wijst van je af: die geef je weg. De pijl eronder wijst
    // naar je toe: die wil je hebben. Het getal ertussen is wat je er na de ruil van
    // overhoudt — groen als er iets bij komt, rood als er iets af gaat.
    for (const [i, soort] of SOORTEN.entries()) {
      const x = P.tellers.x + i * P.tellers.stap;
      laag.addChild(
        pijl(
          ['MENU_139', 'MENU_140', 'MENU_141'],
          x,
          P.tellers.y + P.tellers.opY,
          P.tellers.breed,
          P.tellers.hoog2,
          () => stap(soort, -1),
        ),
      );
      const heb = opties.regels.voorraad[soort] ?? 0;
      const saldo = saldoVan(soort);
      const label = tekst(
        String(heb + saldo),
        14,
        saldo > 0 ? 0x8fdc6a : saldo < 0 ? 0xe4705a : 0xf6e7c4,
      );
      label.x = x + 24;
      label.y = P.tellers.y + P.tellers.iconY + 3;
      laag.addChild(label);
      laag.addChild(
        pijl(
          ['MENU_067', 'MENU_068', 'MENU_069'],
          x,
          P.tellers.y + P.tellers.neerY,
          P.tellers.breed,
          P.tellers.hoog2,
          () => stap(soort, 1),
        ),
      );
    }

    // --- knoppen
    const knoppen: readonly { soort: Parameters<typeof knop>[0]['soort']; doe: () => void }[] = [
      {
        soort: binnen !== undefined && ongewijzigd ? 'accepteren' : 'aanbieden',
        doe: () => {
          // Onveranderd doorgeven is accepteren; heb je eraan gedraaid, dan is het een
          // tegenvoorstel en gaat het als nieuw aanbod terug.
          if (binnen !== undefined && ongewijzigd) {
            opties.onAntwoord?.(true);
            return;
          }
          if (metBank) {
            const bron = SOORTEN.find((s2) => (geef[s2] ?? 0) > 0);
            const doel = SOORTEN.find((s2) => (wil[s2] ?? 0) > 0);
            if (bron !== undefined && doel !== undefined) {
              opties.onBank(bron, geef[bron] ?? 0, doel);
            }
            return;
          }
          opties.onAanbod({ ...geef }, { ...wil });
        },
      },
      {
        soort: 'nieuw',
        doe: () => {
          for (const s2 of SOORTEN) {
            delete geef[s2];
            delete wil[s2];
          }
        },
      },
      {
        soort: 'annulerenSmal',
        doe: () => {
          if (binnen !== undefined) opties.onAntwoord?.(false);
          else opties.onSluiten();
        },
      },
      {
        soort: 'bank',
        doe: () => {
          metBank = !metBank;
          for (const s2 of SOORTEN) {
            delete geef[s2];
            delete wil[s2];
          }
        },
      },
    ];
    for (const [i, k] of knoppen.entries()) {
      const el = knop({
        soort: k.soort,
        onClick: () => {
          k.doe();
          teken();
        },
      });
      el.x = P.knoppen.plekken[i] ?? 0;
      el.y = P.knoppen.y;
      laag.addChild(el);
    }
  }

  teken();
  return wortel;
}

/** Telt per grondstof hoeveel er in een vorstendom ligt. */
export function voorraadVan(landschappen: readonly { landscape: string; resources: number }[]): Record<
  Resource,
  number
> {
  const uit = Object.fromEntries(SOORTEN.map((s) => [s, 0])) as Record<Resource, number>;
  for (const tegel of landschappen) {
    const soort = LANDSCAPE_RESOURCE[tegel.landscape as keyof typeof LANDSCAPE_RESOURCE];
    if (soort !== undefined) uit[soort] += tegel.resources;
  }
  return uit;
}
